import { ArrowHeadRightIcon, Checkbox, Chip, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { lowercaseFirstLetter, naturalStringSort } from 'utils/stringHelper';
import styles from './_PlanungImportForms.module.scss';

type PlanungImportSubjectsFormRowProps = {
  uuid: string;
  selected: boolean;
  name: string;
  globalSubjects: { uuid: string; name: string }[];
  subjects: { uuid: string; name: string }[];
  selectedGlobal: { value: string; label: string } | null;
  selectedSubject: { value: string; label: string } | null;
  connected: boolean;
  onSelect: (isSelected: boolean) => void;
  onGlobalSelect: (uuid: string | null) => void;
  onSubjectSelect: (uuid: string | null) => void;
};

export const PlanungImportSubjectsFormRow = ({
  uuid,
  selected,
  name,
  globalSubjects,
  subjects,
  selectedGlobal,
  selectedSubject,
  connected,
  onSelect,
  onGlobalSelect,
  onSubjectSelect,
}: PlanungImportSubjectsFormRowProps) => {
  const { t } = useTranslation();

  const globalOptions: SelectOptionType[] = globalSubjects
    .map((s) => ({ value: s.uuid, label: s.name }))
    .sort((a, b) => naturalStringSort(a.label, b.label));
  const subjectOptions: SelectOptionType[] = subjects
    .map((s) => ({ value: s.uuid, label: s.name }))
    .sort((a, b) => naturalStringSort(a.label, b.label));

  return (
    <div className={classNames(styles['form-row'], styles.subjects, { [styles.selected]: selected })}>
      <div>
        <Checkbox
          onChange={(event) => {
            onSelect(event.target.checked);
          }}
          checked={selected}
          name={`check-${uuid}`}
          label={name}
          preventLabelWrapping
        />
      </div>
      <div>
        <ArrowHeadRightIcon />
      </div>
      <div>
        <Select
          dense
          options={globalOptions}
          name={`select-global-${uuid}`}
          onChange={(event) => {
            const selectOption = event as SelectOptionType;
            const uuid = selectOption ? (selectOption.value as string) : null;
            onGlobalSelect(uuid);
          }}
          value={selectedGlobal}
          isSearchable
          menuPosition='fixed'
          disabled={!selected}
          error={selected && !selectedGlobal ? t('validation.required.globalSubject') : undefined}
        />
      </div>
      <div>
        <Select
          dense
          options={subjectOptions}
          name={`select-subject-${uuid}`}
          placeholder={selected ? lowercaseFirstLetter(t('planungImport.willBeCreated')) : undefined}
          onChange={(event) => {
            const selectOption = event as SelectOptionType;
            const uuid = selectOption ? (selectOption.value as string) : null;
            onSubjectSelect(uuid);
          }}
          value={selectedSubject}
          isClearable
          isSearchable
          menuPosition='fixed'
          disabled={!selected}
        />
      </div>
      <div>
        <Chip
          isSmall
          value={connected ? <>&#10003;</> : <>&#8212;</>}
          color={connected ? 'var(--color-white)' : 'var(--color-grey-light)'}
          bgColor={connected ? 'var(--color-primary)' : 'var(--color-grey-lighter)'}
        />
      </div>
    </div>
  );
};
